import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AgentAuthProvider } from "./contexts/AgentAuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAgentRoute from "./components/agent/ProtectedAgentRoute";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import OnboardingImport from "./pages/OnboardingImport";
import InstructorSignup from "./pages/InstructorSignup";
import InvitationSignup from "./pages/InvitationSignup";
import AgentLogin from "./components/agent/AgentLogin";
import MagicLinkVerification from "./components/agent/MagicLinkVerification";
import AgentDashboard from "./components/agent/AgentDashboard";
import AgentEmailSettings from "./components/agent/AgentEmailSettings";
import AgentEmailChangeConfirmation from "./components/agent/AgentEmailChangeConfirmation";
import AgentPortalRedirect from "./components/agent/AgentPortalRedirect";
import PowerUpQuickEntry from "./components/agent/PowerUpQuickEntry";
import moduleLoader from "./services/moduleLoader";
import DynamicRoutes from "./components/DynamicRoutes";
import HomeRoute from "./components/HomeRoute";
import CoachSignup from "./pages/CoachSignup";

import { useAuth } from "./contexts/AuthContext";
import { agents as agentsApi } from "./utils/api";

// Component that checks if org needs onboarding and redirects
const LayoutWithOnboardingCheck = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setCheckingOnboarding(false);
        return;
      }

      // Skip check for super admins
      const normalizedRoles = Array.isArray(user.roles)
        ? user.roles.map((role) => role?.toLowerCase()).filter(Boolean)
        : [];
      const isSuperAdmin =
        user.role?.toLowerCase() === "super_admin" ||
        normalizedRoles.includes("super_admin");

      if (isSuperAdmin) {
        setCheckingOnboarding(false);
        return;
      }

      try {
        const response = await agentsApi.getOnboardingStatus();
        if (response.data.needsOnboarding) {
          setNeedsOnboarding(true);
          navigate("/onboarding", { replace: true });
        }
      } catch (error) {
        console.error("Failed to check onboarding status:", error);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user, navigate]);

  if (checkingOnboarding) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (needsOnboarding) {
    return null; // Will redirect
  }

  return <Layout>{children}</Layout>;
};

function App() {
  useEffect(() => {
    // Initialize module loader
    moduleLoader.init().catch(console.error);
  }, []);

  return (
    <AgentAuthProvider>
      <AuthProvider>
        <Router>
          <Routes>
                <Route path="/" element={<HomeRoute />} />
                {/* Coach Authentication Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/coach-signup" element={<CoachSignup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Agent Authentication Routes */}
                <Route path="/agent/login" element={<AgentLogin />} />
                <Route
                  path="/agent/verify/:token"
                  element={<MagicLinkVerification />}
                />
                <Route path="/agent/verify" element={<MagicLinkVerification />} />
                <Route path="/agent/email/confirm" element={<AgentEmailChangeConfirmation />} />

                {/* Agent Portal URL Routes */}
                <Route
                  path="/agent/portal/:slug"
                  element={<AgentPortalRedirect />}
                />

                {/* Agent Protected Routes */}
                <Route
                  path="/agent/email"
                  element={
                    <ProtectedAgentRoute>
                      <AgentEmailSettings />
                    </ProtectedAgentRoute>
                  }
                />
                <Route
                  path="/agent/dashboard"
                  element={
                    <ProtectedAgentRoute>
                      <AgentDashboard />
                    </ProtectedAgentRoute>
                  }
                />
                <Route
                  path="/power-up"
                  element={
                    <ProtectedAgentRoute>
                      <PowerUpQuickEntry />
                    </ProtectedAgentRoute>
                  }
                />

                {/* Public instructor signup routes */}
                <Route path="/instructor-signup" element={<InstructorSignup />} />
                <Route
                  path="/instructor-signup/:token"
                  element={<InstructorSignup />}
                />

                {/* Public invitation signup route */}
                <Route path="/invitation-signup" element={<InvitationSignup />} />

                {/* Onboarding route (protected but no layout) */}
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <OnboardingImport />
                    </ProtectedRoute>
                  }
                />

                {/* Coach Protected Routes (existing functionality) */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <LayoutWithOnboardingCheck>
                        <DynamicRoutes />
                      </LayoutWithOnboardingCheck>
                    </ProtectedRoute>
                  }
                />
          </Routes>
        </Router>
      </AuthProvider>
    </AgentAuthProvider>
  );
}

export default App;
